<!-- 文本省略 -->
<template>
  <div
    ref="rootRef"
    :class="[ns.b(), ns.m(size), ns.m(type)]"
    :style="localEllipsisStyle"
  >
    <template v-if="showTooltip">
      <el-tooltip v-bind="localTooltip" :disabled="!data.collapsed">
        <div :class="ns.e('content')">
          {{ data.collapsed ? data.localText : ellipsisText
          }}{{ data.collapsed && showEllipsis ? '...' : ''
          }}<a
            v-if="showArrow"
            :class="ns.e('arrow')"
            @click.stop="handleCollapse"
            ><el-icon
              v-if="!showText && !hideText"
              :class="{
                [ns.e('arrow-icon')]: true,
                [ns.is('expand')]: !data.collapsed,
              }"
            >
              <ArrowDown /> </el-icon
            ><span v-else>{{ data.collapsed ? showText : hideText }}</span></a
          >
        </div>
      </el-tooltip>
    </template>
    <template v-else>
      <div :class="ns.e('content')">
        {{ data.collapsed ? data.localText : ellipsisText
        }}{{ data.collapsed && showEllipsis ? '...' : ''
        }}<a v-if="showArrow" :class="ns.e('arrow')" @click="handleCollapse"
          ><el-icon
            v-if="!showText && !hideText"
            :class="{
              [ns.e('arrow-icon')]: true,
              [ns.is('expand')]: !data.collapsed,
            }"
          >
            <ArrowDown /> </el-icon
          ><span v-else>{{ data.collapsed ? showText : hideText }}</span></a
        >
      </div>
    </template>
    <div :class="ns.e('virtual')" />
    <a v-if="showArrow" ref="virtualArrowRef" :class="ns.e('virtual-arrow')"
      ><el-icon v-if="!showText && !hideText" :class="ns.e('arrow-icon')">
        <ArrowDown /> </el-icon
      ><span v-else>{{ showText }}</span></a
    >
  </div>
</template>

<script lang="ts" setup>
// TODO arrow 鼠标悬浮-是否不展示tooltip。
// 如果需要：只需考虑 tooltip 的 trigger = 'hover' 的情况
// 处理：监听arrow的 mouseenter事件，手动触发parent（tooltip referer）的mouseleave事件
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  useSlots,
  watch,
  watchEffect,
} from 'vue'
import { useResizeObserver } from '@vueuse/core'
import { useNamespace } from '@element-plus/hooks'
import { NOOP } from '@element-plus/utils'
import { ElTooltip } from '@element-plus/components/tooltip'
import { ElIcon } from '@element-plus/components/icon'
import { ArrowDown } from '@element-plus/icons-vue'
import {
  getCurrentStyle,
  getStyleValue,
  isBoolean,
  isString,
  mergeStyle,
} from './dom'
import { textEllipsisEmits, textEllipsisProps } from './text-ellipsis'
import type { EllipsisTooltipProps } from './text-ellipsis'
import type { StyleObjectInterface } from './dom'
import type { VNode } from 'vue'

type EllipsisTooltip = EllipsisTooltipProps | { content: string }

defineOptions({
  name: 'ElTextEllipsis',
})

const ns = useNamespace('text-ellipsis')

const props = defineProps(textEllipsisProps)
const emits = defineEmits(textEllipsisEmits)
const slots = useSlots()

const data = reactive({
  collapsed: true, // 收起/展开 - showArrow
  slotText: '', // 使用插槽文本
  localText: '', // 省略后的文本
  _beforeBreakText: '', // 未拆分单词的文本
  _rows: 0, // 实际文本行数
  _height: 0, // 实际文本高度
  _chars: 0, // 实际文本字符长度
  _virtualPad: 0, // 虚拟节点右间距 - 承载arrow
})

const rootRef = ref<HTMLElement | null>(null)
const virtualArrowRef = ref<HTMLElement | null>(null)
let stopResizeListener: any = NOOP

// 需要省略的文本
const ellipsisText = computed<string>(() => {
  // 优先使用content
  if (typeof props.content !== 'undefined') return props.content
  // 支持属性设置内容或者文本节点 - 只取第一个节点
  // const defaultVnode: VNode | null = slots.default ? slots.default()[0] : null
  // return defaultVnode && defaultVnode.el ? defaultVnode.el.outerText.trim() : ''
  return data.slotText
})

// 是否需要ellipsis
const showEllipsis = computed<boolean>(() => {
  return (
    !!ellipsisText.value.length &&
    !!data.localText.length &&
    data.localText.length < ellipsisText.value.length
  )
})

// ellipsis样式
const localEllipsisStyle = computed<StyleObjectInterface>(() => {
  const styleObj: StyleObjectInterface = {}
  if (!props.breakWord) {
    // 单词换行
    styleObj['wordBreak'] = 'break-word'
    // styleObj['wordWrap'] = 'break-word';
  } else {
    styleObj['wordBreak'] = 'break-all'
    // styleObj['wordWrap'] = 'unset';
  }
  if (props.lineHeight) {
    // 单独设置行高 - 默认像素单位
    styleObj['lineHeight'] = `${props.lineHeight}px`
  }
  if (props.height) {
    // 单独设置高度 - 默认像素单位
    styleObj['height'] = `${props.height}px`
  }
  return mergeStyle(props.ellipsisStyle || '', styleObj)
})

// 限制高度
const limitHeight = computed<number>(() => {
  if (props.height) return props.height
  const height = getStyleValue(props.ellipsisStyle, 'height')
  return Number.parseInt(height, 10) || 0
})

// 判断省略优先级 - 行数 r < 字符数 c < 高度 h
const limitType = computed<string>(() => {
  if (limitHeight.value) return 'h'
  if (props.chars) return 'c'
  return 'r'
})

// tooltip
const localTooltip = computed<boolean | EllipsisTooltip>(() => {
  if (!props.tooltip) return false
  const defaultTooltip = {
    effect: 'light',
    placement: 'top',
    content: ellipsisText.value,
  }

  return isBoolean(props.tooltip)
    ? defaultTooltip
    : { ...defaultTooltip, ...(props.tooltip as EllipsisTooltipProps) }
})

// show tooltip
const showTooltip = computed<boolean>(() => {
  return (
    !!localTooltip.value &&
    (props.tooltipTrigger === 'always' ||
      (props.tooltipTrigger === 'ellipsis' && showEllipsis.value))
  )
})

const showText = computed<string>(() => {
  if (props.showArrow && props.arrowText) {
    return Array.isArray(props.arrowText)
      ? (props.arrowText[0] as string)
      : (props.arrowText as string)
  }
  return ''
})

const hideText = computed<string>(() => {
  if (props.showArrow && props.arrowText) {
    return Array.isArray(props.arrowText)
      ? (props.arrowText[1] as string) || (props.arrowText[0] as string)
      : (props.arrowText as string)
  }
  return ''
})

watch(
  [
    ellipsisText,
    limitHeight,
    () => props.chars,
    () => props.rows,
    () => props.ellipsisStyle,
    () => props.breakWord,
  ],
  () => {
    calculate(ellipsisText.value, limitType.value)
  }
)

watch(
  showEllipsis,
  (val) => {
    emits('ellipsis', val)
  },
  { immediate: true }
)

watch(
  () => [props.showArrow, props.arrowText],
  () => {
    nextTick(() => {
      update()
    })
  }
)

onMounted(() => {
  if (props.showArrow) {
    data._virtualPad = virtualArrowRef.value?.getBoundingClientRect().width ?? 0
  }
  watchEffect(() => {
    data.slotText = getSlotText()
  })
  calculate(ellipsisText.value, limitType.value)
  stopResizeListener = useResizeObserver(rootRef, handleResize)
})

onBeforeUnmount(() => {
  stopResizeListener && stopResizeListener.stop && stopResizeListener.stop()
})

// function
function update(): void {
  if (props.showArrow) {
    data._virtualPad = virtualArrowRef.value?.getBoundingClientRect().width ?? 0
  }
  data.slotText = getSlotText()
  calculate(ellipsisText.value, limitType.value)
}

// 窗口尺寸改变
function handleResize(): void {
  calculate(ellipsisText.value, limitType.value)
}

// 计算省略文本-带拆分单词
function calculate(text: string, limitType: string): void {
  if (!text) {
    data.localText = ''
    data._beforeBreakText = ''
    data._rows = 0
    data._height = 0
    data._chars = 0
  } else {
    // 实际计算
    if (isNeed(text)) {
      const hiddenDom = (
        rootRef.value as HTMLElement
      ).querySelector<HTMLElement>(`.${ns.e('virtual')}`)
      if (hiddenDom) {
        hiddenDom.innerHTML = ''
        // 计算英文字符 与 中文字符的高度差
        let offset = 0 // 英文字符 与 中文字符的高度差
        let heightStr = ''
        const currentLineHeightStr = getCurrentStyle(hiddenDom, 'line-height')
        const currentLineHeight = Number.parseInt(currentLineHeightStr, 10)
        // 未设置行高，需要计算行高误差
        if (!props.lineHeight && !currentLineHeight) {
          hiddenDom.innerHTML = 'x'
          heightStr = getCurrentStyle(hiddenDom, 'height')
          const lowerCaseHeight = Number.parseInt(heightStr, 10)
          hiddenDom.innerHTML = 'X'
          heightStr = getCurrentStyle(hiddenDom, 'height')
          const upperCaseHeight = Number.parseInt(heightStr, 10)
          hiddenDom.innerHTML = '中'
          heightStr = getCurrentStyle(hiddenDom, 'height')
          const chinessHeight = Number.parseInt(heightStr, 10)
          offset = Math.max(
            chinessHeight - upperCaseHeight,
            chinessHeight - lowerCaseHeight,
            upperCaseHeight - lowerCaseHeight,
            0
          )
          hiddenDom.innerHTML = ''
        }
        // 开始计算
        let step = props.step
        let start = 0
        let end = false
        let currentHeight = 0
        let lastCurrentHeight = 0
        let currentRows = 0
        let lastCurrentRows = 0
        if (props.showArrow) {
          hiddenDom.style.paddingRight = `${data._virtualPad + 8}px` // 8 - margin-left
        }
        while (!end && start < text.length) {
          // 更新高度
          lastCurrentHeight = currentHeight
          lastCurrentRows = currentRows
          // 重新计算
          start += step
          hiddenDom.innerHTML = `${text.slice(
            0,
            Math.max(0, Math.min(start, text.length))
          )}...`
          const currentHeightStr = getCurrentStyle(hiddenDom, 'height')
          currentHeight = Number.parseInt(currentHeightStr, 10)
          // 统计行数
          if (currentHeight - offset > lastCurrentHeight) {
            if (currentLineHeight) {
              currentRows = Math.ceil(
                (currentHeight - currentLineHeight / 2) / currentLineHeight
              )
            } else {
              currentRows++
            }
          }
          // 根据限制类型处理
          switch (limitType) {
            case 'h': {
              end = currentHeight > limitHeight.value
              break
            }
            case 'c': {
              end = start > (props.chars || 1)
              break
            }
            case 'r': {
              end = currentRows > props.rows
              break
            }
          }
        }
        // 步长处理
        if (end) {
          let compareEnd = false
          const compareHeight = currentHeight
          let compareRows = currentRows
          while (!compareEnd && step >= 0) {
            // 重新计算
            start--
            hiddenDom.innerHTML = `${text.slice(
              0,
              Math.max(0, Math.min(start, text.length))
            )}...`
            const currentCompareHeightStr = getCurrentStyle(hiddenDom, 'height')
            const currentCompareHeight = Number.parseInt(
              currentCompareHeightStr,
              10
            )
            if (currentCompareHeight + offset < compareHeight) {
              if (currentLineHeight) {
                compareRows = Math.ceil(
                  (currentCompareHeight - currentLineHeight / 2) /
                    currentLineHeight
                )
              } else {
                compareRows--
              }
            }
            // 根据限制类型处理
            switch (limitType) {
              case 'h': {
                compareEnd = currentCompareHeight + offset < compareHeight
                break
              }
              case 'c': {
                compareEnd = start <= (props.chars || 1)
                break
              }
              case 'r': {
                compareEnd = compareRows <= props.rows
                break
              }
            }
            step--
          }
        }
        // 对最终结果整合
        data._rows = lastCurrentRows
        data._height = lastCurrentHeight
        data.localText = end ? text.slice(0, Math.max(0, start)) : text
        data._chars = data.localText.length
        hiddenDom.innerHTML = ''
        // 单词拆分
        !props.breakWord && breakTextWord()
      }
    } else {
      data.localText = text
      data._beforeBreakText = text
    }
  }
}

// 是否需要计算
function isNeed(text: string): boolean {
  let flag = true
  if (!rootRef.value) return false
  const hiddenDom = rootRef.value.querySelector<HTMLElement>(
    `.${ns.e('virtual')}`
  )
  if (!hiddenDom) return false
  hiddenDom.innerHTML = text
  const currentHeightStr = getCurrentStyle(hiddenDom, 'height')
  const currentHeight = Number.parseInt(currentHeightStr, 10)
  switch (limitType.value) {
    case 'h': {
      flag = currentHeight > limitHeight.value
      if (!flag) {
        data._height = currentHeight
      }
      break
    }
    case 'c': {
      flag = text.length > (props.chars || 1)
      if (!flag) {
        data._chars = text.length
      }
      break
    }
    case 'r': {
      const currentLineHeightStr = getCurrentStyle(hiddenDom, 'line-height')
      const currentLineHeight = Number.parseInt(currentLineHeightStr, 10)
      const lineHeight = props.lineHeight || currentLineHeight
      if (lineHeight) {
        const rows = Math.ceil(currentHeight / lineHeight)
        flag = rows > (props.rows || 1)
        if (!flag) {
          data._rows = rows
        }
      }
      break
    }
  }
  return flag
}

// 按单词换行 - 默认拆分单词换行
// TODO 优化拆分单词规则
function breakTextWord(): void {
  if (showEllipsis.value && limitType.value !== 'c' && !props.breakWord) {
    let start = 0
    let end = false
    while (!end && start < data.localText.length) {
      start++
      const subMatch = data.localText.slice(-start).match(/^(\s?)([a-zA-Z]*)$/)
      if (!subMatch) {
        end = true
        start = 0
      } else if (subMatch[1] !== '') {
        // 找到空格分隔
        end = true
      }
    }
    // 截取
    if (
      end &&
      start !== 0 &&
      new RegExp('[a-zA-Z]').test(
        ellipsisText.value[data.localText.length] || ''
      )
    ) {
      data.localText = data.localText.slice(
        0,
        Math.max(0, data.localText.length - start)
      )
      data._chars = data._chars - start
    }
  }
}

function getSlotText(): string {
  if (typeof props.content !== 'undefined') return ''
  // 支持属性设置内容或者文本节点 - 只取第一个节点
  const defaultVnode: VNode | null = slots.default ? slots.default()[0] : null
  return defaultVnode && isString(defaultVnode.children)
    ? (defaultVnode.children as string).trim()
    : ''
}

function handleCollapse(): void {
  data.collapsed = !data.collapsed
}

defineExpose({
  limitType,
  update,
  calculate,
})
</script>
